<template>
  <div v-show="show" class="company-layout" :class="showCustomFooterNavigation && 'has-custom-footer'" :style="rootStyle">
    <div class="layout-head-wrapper">
      <PersonalCommonHeader
        :class="fixedHeader && 'fixed-header'"
        :companyId="companyId"
        :isMSDCustom="isMSDCustom"
        :customPage="customPage"
        @changeLanguage="handleChangeLanguage"
      ></PersonalCommonHeader>
    </div>
    <div class="layout-banner">
      <div v-if="bannerSetting" class="banner-wrapper">
        <Banner
          class="banner"
          hiddenIndicator
          hide-video-controls
          slide-arrow="hover"
          :bannerSetting="bannerSetting"
          :loop-single-video="isSingleVideoBanner"
        />
      </div>
    </div>
    <div class="layout-body">
      <slot v-if="isMSDCustom" name="bodyContent" class="body-content"></slot>
      <div v-else>
        <router-view v-if="!loading && company.id" class="body-content" :company="company" :webPageItems="webPageItems" @getJDChatbotContext="getJDChatbotContext"></router-view>
      </div>
    </div>
    <CommonFooter v-if="showCustomFooterNavigation" class="custom-footer"></CommonFooter>
    <Chatbot
      v-if="showChatbot || showAiChatbot"
      btnDialogToggleBootem="24px"
      btnDialogToggleRight="10px"
      enableCloseBtnToDialog
      :useAiChatbot="showAiChatbot"
      :generateUserId="userId"
      :currentAccountId="currentAccount.id"
      :showChatbotMessageBox="showChatbotMessageBox"
      :company="company"
      :showReferral="jobDetailChatbotContent.showReferral"
      :hasReferralIcon="jobDetailChatbotContent.submitReferral"
      :context="jobDetailChatbotContent.context"
      :jobId="jobDetailChatbotContent.jobId"
      @applyJob="handleApplyJob"
    />
  </div>
</template>

<script>
import { addI18nProperty, getI18nPropertyValue } from '@/i18n'
import { mapActions, mapGetters } from 'vuex'
import { COMPANY_PAGE_TYPE } from '@/constant/company'
import { msdPcJobListRouteMap } from '../msdCustomUrl/pc/route'
import { msdMobileJobListRouteMap } from '../msdCustomUrl/mobile/route'
import _ from 'lodash'
import Chatbot from '@/components/job/chatbot/index.vue'
import Banner from '@/components/company/detail/banner.vue'
import { deviceType } from '@/utils/common'
import CommonFooter from '@/components/layout/footer/commonFooter.vue'
import PersonalCommonHeader from '@/components/layout/header/personalCommonHeader.vue'
import mixin from '@/views/personalNew/layout/mixin'

export default {
  components: {
    Chatbot,
    Banner,
    CommonFooter,
    PersonalCommonHeader
  },
  props: {
    customCompanyId: {
      required: false
    },
    isMSDCustom: {
      type: Boolean,
      default: false
    },
    customBrandingRoute: {
      type: Object,
      default: () => {}
    },
    customLandingRoute: {
      type: Object,
      default: () => {}
    },
    companyBaseInfoResp: {
      required: false
    },
    companyBrandingPageInfoResp: {
      required: false
    }
  },
  metaInfo () {
    return {
      style: [{
        cssText: `
          body {
            font-size: 16px;
            color: #303133;
            line-height: 22px;
            --custom-color: ${this.company.custom_color};
          }
          * {
            box-sizing: border-box;
          }
        `
      }],
      link: this.metaInfoLinkList
    }
  },
  mixins: [mixin],
  data () {
    return {
      show: false,
      loading: false,
      company: {
        logo: null
      },
      customPage: {},
      showChatbot: false,
      showAiChatbot: false,
      showChatbotMessageBox: false,
      userId: '',
      brandingPageWebpageItems: [],
      landingPageWebpageItems: [],
      metaInfoLinkList: [],
      jobDetailChatbotContent: {
        context: null,
        jobId: null,
        showReferral: false,
        submitReferral: false
      }
    }
  },
  computed: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapGetters('company', ['companyThemeSetting']),
    currentAccount () {
      return this.getCurrentAccount
    },
    msdJobListRouteMap () {
      return deviceType.isMobile ? msdMobileJobListRouteMap : msdPcJobListRouteMap
    },
    companyId () {
      return this.customCompanyId || this.$route.params.companyId
    },
    pageType () {
      return this.$route.meta?.pageType
    },
    currentPageNum () {
      const pageNumber = '10'
      if (this.$route.params.pageNum) {
        return this.$route.params.pageNum
      } else if (this.$route.meta?.pageType === COMPANY_PAGE_TYPE.JOB_LIST || this.$route.meta?.pageType === COMPANY_PAGE_TYPE.JOB_DETAIL) {
        return '10'
      }
      return pageNumber
    },
    landingPage () {
      return this.customPage?.landing_page
    },
    brandingPage () {
      return this.customPage?.branding_page
    },
    bannerSetting () {
      const pageType = this.$route.meta?.pageType
      const isBranding = pageType === COMPANY_PAGE_TYPE.BRANDING
      const isLanding = pageType === COMPANY_PAGE_TYPE.LANDING
      const isJobList = pageType === COMPANY_PAGE_TYPE.JOB_LIST
      const isJobDetail = pageType === COMPANY_PAGE_TYPE.JOB_DETAIL
      if (isBranding) {
        return this.brandingPage?.pc_branding_banner
      } else if (isLanding) {
        return this.landingPage?.pc_branding_banner
      } else if (isJobList) {
        return this.customPage?.job_list_page?.pc_banner
      } else if (isJobDetail) {
        return this.customPage?.job_detail_page?.pc_banner
      }
      return null
    },
    isSingleVideoBanner () {
      const items = this.bannerSetting?.items || []
      return items.length === 1 && this.isBannerVideoItem(items[0])
    },
    webPageItems () {
      const pageType = this.$route.meta?.pageType
      const isBranding = pageType === COMPANY_PAGE_TYPE.BRANDING
      return isBranding ? this.brandingPageWebpageItems : this.landingPageWebpageItems
    },
    customMenuList () {
      return _.map(this.companyThemeSetting?.companyDetailSetting?.menuList, (item, index) => {
        return {
          path: item.link || '',
          get label () {
            return getI18nPropertyValue(item, 'label', 'enLabel')
          },
          isCustomMenu: true
        }
      })
    },
    showCustomFooterNavigation () {
      const footerContent = this.companyThemeSetting?.footerSetting?.footerContent
      if (footerContent && footerContent.length > 0) {
        return true
      }
      return false
    },
    fixedHeader () {
      return this.hasCustomTheme ? this.companyThemeSetting.companyDetailSetting?.fixedHeader : false
    },
    rootStyle () {
      const style = {
        '--header-height': this.headerSetting.headerHeight ? `${this.headerSetting.headerHeight}px` : null
      }
      return style
    }
  },
  beforeRouteEnter (to, from, next) {
    if (deviceType.isMobile && _.includes([COMPANY_PAGE_TYPE.BRANDING, COMPANY_PAGE_TYPE.LANDING], to.meta?.pageType)) {
      next({ path: `/recruiting/company/${to.params.companyId}/${to.meta?.pageType}`, query: to.query, hash: to.hash })
    } else if (deviceType.isMobile && _.includes([COMPANY_PAGE_TYPE.JOB_LIST], to.meta?.pageType)) {
      next({ path: `/mobile-web-more-jobs/${to.params.companyId}`, query: to.query, hash: to.hash })
    }
    next()
  },
  created () {
    this.initPageData()
  },
  methods: {
    ...mapActions('company', ['getCompanyBaseInfo', 'getCompanyBrandingPageInfo']),
    ...mapActions('account', ['getSessionAccountInfo']),
    async initPageData () {
      if (!(deviceType.isMobile && _.includes([COMPANY_PAGE_TYPE.JOB_DETAIL], this.$route.meta?.pageType))) {
        this.show = true
      }
      this.loading = true
      this.$eventBus.$emit('showLoading', true)
      try {
        await Promise.all([
          this.getSessionAccountInfo(),
          this.loadCompanyInfo(),
          this.loadCompanyPageInfo()
        ])
      } finally {
        this.loading = false
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadCompanyInfo () {
      let resp = null
      if (this.isMSDCustom) {
        resp = await this.companyBaseInfoResp
      } else {
        resp = await this.getCompanyBaseInfo({ id: this.companyId, params: { simple_info: 1 } })
      }
      if (resp) {
        this.company = addI18nProperty(resp, 'i18nName', 'cn_name', 'name')
        addI18nProperty(this.company.root_company, 'i18nName', 'cn_name', 'name')
        this.showAiChatbot = resp.show_ai_chatbot
        if (!this.showAiChatbot) {
          this.showChatbot = resp.show_chatbot
          if (this.showChatbot) {
            let baseUrl = process.env.VUE_APP_STYLE_BASE_URL
            if (baseUrl.endsWith('/')) {
              baseUrl = baseUrl.substring(0, baseUrl.length - 1)
            }
            this.metaInfoLinkList.push({
              rel: 'stylesheet',
              href: `${baseUrl}/static/style/henkel-bot.css`
            })
          }
        }
      }
    },
    async loadCompanyPageInfo () {
      let resp = null
      if (this.isMSDCustom) {
        resp = await this.companyBrandingPageInfoResp
        this.$watch('companyBrandingPageInfoResp', async (pageInfoResp) => {
          const resp = await pageInfoResp
          this.refreshCompanyPageInfo(resp)
        })
      } else {
        resp = await this.getCompanyBrandingPageInfo({
          company_id: this.companyId,
          language_code: this.$i18n.locale === 'en' ? 'en' : 'zh-cn'
        })
      }
      if (resp) {
        this.refreshCompanyPageInfo(resp)
      }
    },
    refreshCompanyPageInfo (info) {
      const brandingPage = info.branding_page
      const landingPage = info.landing_page
      if (brandingPage) {
        addI18nProperty(brandingPage, 'i18nName', 'menu_cn_name', 'menu_en_name')
        this.brandingPageWebpageItems = brandingPage.webpage_items ? JSON.parse(brandingPage.webpage_items) : []
      } else {
        let url = ''
        if (_.includes([COMPANY_PAGE_TYPE.JOB_DETAIL], this.$route.meta?.pageType)) {
          url = `/recruiting/company/${this.companyId}/job/${this.$route.params.jobId}/job-detail`
        } else {
          if (this.isMSDCustom) {
            const route = _.get(this.msdJobListRouteMap, this.companyId)
            url = route.path
          } else {
            url = `/recruiting/company/${this.companyId}/job-list`
          }
        }
        if (url !== this.$route.path) {
          this.$router.push({
            path: url,
            query: this.$route.query,
            hash: this.$route.hash
          })
        }
      }
      if (landingPage) {
        addI18nProperty(landingPage, 'i18nName', 'menu_cn_name', 'menu_en_name')
        this.landingPageWebpageItems = landingPage.webpage_items ? JSON.parse(landingPage.webpage_items) : []
      }
      this.customPage = info
      if (!_.isEmpty(this.customMenuList) && this.pageType === COMPANY_PAGE_TYPE.BRANDING) {
        const customMenuPathList = _.chain(this.customMenuList).filter('path').map('path').value()
        const patternList = [
          /^.*\/company-detail-new\/.+$/
        ]
        if (this.isMSDCustom) {
          patternList.push(/^.*\/msdchina\/about.*$/)
        } else {
          patternList.push(...[
            /^.*\/recruiting\/company-detail\/.+$/,
            /^.*\/recruiting\/company\/.+\/branding\/10.*$/
          ])
        }
        if (!_.some(customMenuPathList, customPath => _.some(patternList, pattern => pattern.test(customPath)))) {
          if (this.isCustomMenu) {
            this.$router.replace({ path: '/msdchina/professionals', query: this.$route.query })
          } else {
            this.$router.replace({ path: `/recruiting/company/${this.companyId}/job-list`, query: this.$route.query })
          }
        }
      }
    },
    async handleChangeLanguage () {
      this.$eventBus.$emit('showLoading', true)
      try {
        if (this.isMSDCustom) {
          this.$emit('changeLanguage')
        } else {
          await this.loadCompanyPageInfo()
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    getJDChatbotContext (contentInfo) {
      this.showChatbot = false
      this.$nextTick(() => {
        this.jobDetailChatbotContent = contentInfo
        this.showChatbot = this.company.show_chatbot
      })
    },
    isBannerVideoItem (item) {
      return item?.resource_type === 'video'
    },
    handleApplyJob () {
      this.$eventBus.$emit('applyJob')
    }
  },
  watch: {
    currentPageNum () {
      this.initPageData()
    }
  }
}
</script>

<style lang="scss" scoped>
.company-layout {
  min-height: 100vh;

  &.has-custom-footer {
    display: flex;
    flex-direction: column;
  }

  .layout-head-wrapper {
    height: var(--header-height, 124px);

    .layout-head {
      height: var(--header-height, 124px);
      line-height: var(--header-height, 124px);
      padding: 0 106px;

      ::v-deep {
        .company-info {
          .has-company-logo {
            width: var(--logo-width, 80px);
            height: var(--logo-width, 80px);
          }
        }

        .menu-list {
          margin-left: 126px;
        }

        &.center-style{
          .menu-list {
            margin-left: 0;
          }
        }
      }
    }

    .fixed-header {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 3;
    }
  }

  .layout-banner {
    .banner-wrapper {
      .banner {
        display: block;
        width: 100%;
        ::v-deep {
          .slide-container {
            .el-carousel__item {
              display: block;

              .slide-item {
                width: 100%;
              }

              img,
              video {
                width: 100%;
              }

              video {
                object-fit: cover;
              }
            }
          }
        }
      }
    }
  }

  .layout-body {
    padding: 40px 106px 24px;

    .body-content {
      margin: 0 auto;
    }
  }

  .custom-footer {
    margin-top: auto;
  }
}
</style>
